import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../lib/ep';
import { useSimpleFilter } from '../../lib/hooks';

import ActionButton, { SecondaryButton, SquaredButton } from './ActionButton';
import ButtonCollection from './ButtonCollection';
import ConfirmButton from './ConfirmButton';
import FieldLabel from './FieldLabel';
import FloatingLayer from './FloatingLayer';
import SectionTitle from './SectionTitle';
import SectionSubtitle from './SectionSubtitle';
import SearchField from './SearchField';
import SortableItems from './SortableItems';

const filterFunc = (item, value) => {
	let regex = new RegExp(value, 'i');
	return regex.test(item.name);
};

const FullWrapper = ({ selected_items_ids, ...props }) => {
	const [ display_layer, setDisplayLayer ] = useState(false);

	const confirmSelection = (v) => {
		props.change(v, props.parent_id);
		setDisplayLayer(false);
	};

	return (
		<div class="gui_not_in_top list_in_list">
			<div class="gui_section_header_builder">
				<FieldLabel
					_class={{ other:'block_title' }}
				>{props?.title ?? "Items"}</FieldLabel>

				<ButtonCollection>
					<ActionButton
						_class={{ other:'addButton' }}
						onClick={() => setDisplayLayer(true)}
					>{props?.open_button_label ?? "Add items"}</ActionButton>
				</ButtonCollection>
			</div>
			{props.children}

			{(display_layer) && (
				<PickerLayer
					{...props}
					selected_items_ids={selected_items_ids}
					close={() => setDisplayLayer(false)}
					confirmSelection={confirmSelection}
					intl={props.intl}
				/>
			)}
		</div>
	)
};

const Wrapper = (props) => {
	const status = props?.status ?? false;
	const show_actions = props?.show_actions ?? true;
	const custom_actions = props?.custom_actions ?? false;
	const is_empty = props?.is_empty ?? false;

	let classes = ['gui_sortable_list_item', 'gui_flexbox', 'gui_flex_start', 'gui_flex_col', 'gui_flex_nowrap', 'gui_flex_align_center'];
	if(is_empty) classes.push('is_empty');

	return (
		<li class={classes.join(" ")}>
			<div class="item_header">
				<div class="item_title_container">
					{props.children}
				</div>

				{(show_actions) && (
					<ButtonCollection>
						{(status) ? props.button_on : props.button_off}
					</ButtonCollection>
				)}

				{(custom_actions) && (
					<ButtonCollection>
						{custom_actions}
					</ButtonCollection>
				)}
			</div>
		</li>
	)
};

export const PickerLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ selected_items_ids, setSelectedItemsIds ] = useState(props?.selected_items_ids ?? []);
	const [ items, filterStages ] = useSimpleFilter(props.items, (props?.filterFunc ?? filterFunc));
	const [ display_all_in, displayAllIn ] = useState(props.display_all_in ?? true);
	const [ display_all_out, displayAllOut ] = useState(props.display_all_out ?? true);

	const enable_search = props?.enable_search ?? true;

	const search = (k, v) => {
		filterStages(v)
	};

	const updateItem = (id) => {
		if(!selected_items_ids.includes(id)) {
			setSelectedItemsIds((c) => ([...c, id]));
			return;
		}
		setSelectedItemsIds((c) => (c.filter((i) => (i != id))));
	};

	const addAllItems = () => {
		setSelectedItemsIds((a) => items.map((item) => item.id));
	};

	const removeAllItems = () => {
		setSelectedItemsIds((a) => ([]));
	};

	return (
		<FloatingLayer _class={{size:'small'}} backButton={props.close}>
			<div class="tabs_in_layer stage_list_with_tabs">
				<SectionTitle>{props?.layer_title ?? "Add items"}</SectionTitle>

				{(props.layer_subtitle != undefined && props.layer_subtitle != '') && (
					<SectionSubtitle autotranslate={false}>{props.layer_subtitle}</SectionSubtitle>
				)}

				{(display_all_in || display_all_out || enable_search) && (
					<div class="gui_flexbox_auto gui_flex_space_between gui_not_in_bottom">
						{(display_all_in || display_all_out) && (
							<ButtonCollection>
								{(display_all_in) && (
									<ActionButton
									icon={props?.all_in_icon ?? 'knk-plus'}
									onClick={() => addAllItems()}
									>{props?.all_in_label ?? "All in"}</ActionButton>
								)}
								{(display_all_out) && (
									<ActionButton
										icon={props?.all_out_icon ?? 'knk-minus'}
										onClick={() => removeAllItems()}
									>{props?.all_out_label ?? "All out"}</ActionButton>
								)}
							</ButtonCollection>
						)}

						{(props?.enable_search ?? true) && (
							<SearchField
								name="search"
								_class={{first:true}}
								change={search}
							/>
						)}
					</div>
				)}

				{(items.length > 0) && (
					<ul class="gui_overflowable list_with_tabs">
						{items.map((item, it) => {
							const is_selected = selected_items_ids.includes(item.id);

							let icon = props?.item_icon ?? 'knk-arrow-r-big';
							if(icon === false) icon = 'without_icon';
							
							let item_name = item.name;
							if(props.formatItemName != undefined) item_name = props.formatItemName(item, props.intl);
		
							return (
								<Wrapper
									key={`item_${it}`}
									status={is_selected}
									button_on={<SquaredButton
										color='red'
										icon='knk-off'
										onClick={() => updateItem(item.id)}
										aria-label="Delete item"
									/>}
									button_off={<ActionButton
										color='green'
										icon='knk-add-item'
										onClick={() => updateItem(item.id)}
									>Select</ActionButton>}
								>
									<strong class={`${icon} item_title cutTextOnOverflow`}>
										<span>{item_name}</span>
									</strong>

								</Wrapper>
							)
						})}
					</ul>
				)}

				{(items.length < 1) && (
					<ul class="list_with_tabs">
						<Wrapper
							show_actions={false}
						>
							<span class="without_icon item_title cutTextOnOverflow">{i18n("There are no results.")}</span>
							<span class="additional_info">&nbsp;</span>
						</Wrapper>
					</ul>
				)}

				<ButtonCollection in_floating={true}>
					<ActionButton
						onClick={() => props.confirmSelection(selected_items_ids)}
					>Confirm</ActionButton>

					<SecondaryButton
						onClick={() => props.close()}
					>Close</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const PickerActions = ({ item, ...props }) => {
	const element = item.data;
	return (
		<ConfirmButton
			squared={true}
			color="red"
			icon='knk-off'
			_position="left"
			onClick={()=>props.deleteItem(element.id)}
		/>
	)
};

const MultiPicker = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const changeOrder = (elements) => {
		if(props.change && elements.length > 1) props.change(elements.map((e) => (e.data)).map((item) => item.id));
	};

	const selected_items = props?.selected_items ?? [];
	const empty_message = props?.empty_message ?? false;
	let selected_items_ids = [];
	const sortable_items = [];
	
	const is_sortable = props?.sortable ?? false;
	let icon = props?.item_icon ?? 'knk-arrow-r-big';
	if(icon === false) icon = 'without_icon';
	
	selected_items.map((it) => {
		selected_items_ids.push(it.id);
		let item_name = it.name;
		if(props.formatItemName != undefined) item_name = props.formatItemName(it, props.intl);

		sortable_items.push({
			title:()=>(	<strong class={`${icon} item_title cutTextOnOverflow`}>
				<span>{item_name}</span>
			</strong>),
			icon:icon,
			data:it,
		});
	});
	
	const default_full_wrapper_props = {
		selected_items_ids,
		...props,
	};

	if(is_sortable) {
		return (
			<FullWrapper {...default_full_wrapper_props}>
				<SortableItems
					items={sortable_items}
					onChange={changeOrder}
					actions={<PickerActions 
						deleteItem={(id)=>props.deleteItem(id)} />}
						not_bordered_top={true}
				/>
				
				{(sortable_items.length == 0 && empty_message !== false) && (
					<Wrapper is_empty={true}>
						<p>[ {i18n(empty_message)} ]</p>
					</Wrapper>
				)}
			</FullWrapper>
		)
	}

	return (
		<FullWrapper {...default_full_wrapper_props}>
			<ul class="gui_overflowable list_with_tabs">
				{selected_items.map((item, it) => {
					let icon = props?.item_icon ?? 'knk-arrow-r-big';
					if(icon === false) icon = 'without_icon';
					
					let item_name = item.name;
					if(props.formatItemName != undefined) item_name = props.formatItemName(item, props.intl);

					return (
						<Wrapper
							key={`selected_item_${it}`}
							show_actions={false}
							custom_actions={<ConfirmButton
								squared={true}
								color="red"
								icon='knk-off'
								_position="left"
								onClick={() => props.deleteItem(item.id)}
							/>}
						>
							<strong class={`${icon} item_title cutTextOnOverflow`}>
								<span>{item_name}</span>
							</strong>
						</Wrapper>
					)
				})}

				{(selected_items.length == 0 && empty_message !== false) && (
					<Wrapper is_empty={true}>
						<p>[ {i18n(empty_message)} ]</p>
					</Wrapper>
				)}
			</ul>
		</FullWrapper>
	)
};

export default injectIntl(MultiPicker);